// Imágenes elegidas sin conexión (docs/diseno-libreria-local-first.md §6.10).
// Se guardan en el dispositivo y el campo lleva una referencia local
// (`local:<uuid>`); antes de enviar el cambio, `prepareGroupBlobs` las sube al
// storage y cambia la referencia por el id del servidor. Lo ya subido se
// recuerda, así un reintento no sube dos veces.

import type { OpGroup } from "@heskala/localfirst/core";

export const LOCAL_BLOB_PREFIX = "local:";
const DB = "heskala-lf-blobs";
const STORE = "blobs";

interface StoredBlob {
  id: string;
  blob: Blob;
  createdAt: number;
  /** Id de storage, una vez subido. */
  storageId?: string;
}

export const isLocalBlobRef = (value: unknown): value is string =>
  typeof value === "string" && value.startsWith(LOCAL_BLOB_PREFIX);

function open(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE, { keyPath: "id" });
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function tx<T>(mode: IDBTransactionMode, run: (store: IDBObjectStore) => IDBRequest<T> | void): Promise<T | undefined> {
  const db = await open();
  return new Promise((resolve, reject) => {
    const t = db.transaction(STORE, mode);
    const req = run(t.objectStore(STORE));
    t.oncomplete = () => {
      db.close();
      resolve(req ? (req.result as T) : undefined);
    };
    t.onerror = () => {
      db.close();
      reject(t.error);
    };
  });
}

/** Guarda la imagen en el dispositivo y devuelve su referencia local. */
export async function putLocalBlob(blob: Blob): Promise<string> {
  const id = `${LOCAL_BLOB_PREFIX}${crypto.randomUUID()}`;
  await tx("readwrite", (s) => s.put({ id, blob, createdAt: Date.now() } satisfies StoredBlob));
  return id;
}

export async function getLocalBlob(id: string): Promise<StoredBlob | undefined> {
  return tx<StoredBlob>("readonly", (s) => s.get(id));
}

/** Borra todas las imágenes guardadas (cierre de sesión). */
export async function clearLocalBlobs(): Promise<void> {
  await tx("readwrite", (s) => s.clear());
}

/** Referencias locales dentro de un valor (texto, arreglo o arreglo en JSON). */
function mapRefs(value: unknown, fn: (ref: string) => string): unknown {
  if (isLocalBlobRef(value)) return fn(value);
  if (Array.isArray(value)) return value.map((v) => mapRefs(v, fn));
  if (typeof value === "string" && value.startsWith("[") && value.includes(LOCAL_BLOB_PREFIX)) {
    try {
      const list = JSON.parse(value) as unknown;
      if (Array.isArray(list)) return JSON.stringify(list.map((v) => mapRefs(v, fn)));
    } catch {
      /* no era JSON */
    }
  }
  return value;
}

export function localRefsIn(group: OpGroup): string[] {
  const refs = new Set<string>();
  for (const op of group.ops) {
    for (const value of Object.values(op.set ?? {})) mapRefs(value, (ref) => (refs.add(ref), ref));
    for (const delta of Object.values(op.sets ?? {})) mapRefs(delta.add ?? [], (ref) => (refs.add(ref), ref));
  }
  return [...refs];
}

/**
 * Sube las imágenes locales de un grupo (las que faltan) y devuelve el grupo
 * con los ids del servidor. `upload` lanza si no hay red: el motor reintenta.
 */
export async function prepareGroupBlobs(group: OpGroup, upload: (blob: Blob) => Promise<string>): Promise<OpGroup> {
  const refs = localRefsIn(group);
  if (refs.length === 0) return group;
  const ids = new Map<string, string>();
  for (const ref of refs) {
    const stored = await getLocalBlob(ref);
    if (!stored) throw new Error("Una imagen guardada sin conexión ya no está en este dispositivo");
    if (!stored.storageId) {
      stored.storageId = await upload(stored.blob);
      await tx("readwrite", (s) => s.put(stored));
    }
    ids.set(ref, stored.storageId);
  }
  const swap = (ref: string) => ids.get(ref) ?? ref;
  return {
    ...group,
    ops: group.ops.map((op) => ({
      ...op,
      ...(op.set ? { set: Object.fromEntries(Object.entries(op.set).map(([k, v]) => [k, mapRefs(v, swap)])) } : {}),
      ...(op.sets
        ? {
            sets: Object.fromEntries(
              Object.entries(op.sets).map(([k, d]) => [k, { ...d, ...(d.add ? { add: mapRefs(d.add, swap) as unknown[] } : {}) }]),
            ),
          }
        : {}),
    })),
  };
}
